import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/VpnKeyOutlined';
import { useNotify } from 'react-admin';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { shortDateTime } from '../../shared/dateTime';
import {
  fetchMyExchangeAccounts,
  removeMyExchangeAccount,
  saveMyExchangeAccount,
  type MyExchangeAccount,
} from './myExchangeAccountsApi';

/** Maska zapisanej wartości — jak w magazynie poświadczeń (wartości nie ma na ekranie). */
export const SAVED_MASK = '••••••••';

/**
 * „Moje konta giełdowe” w menu użytkownika — konto imienne tradera do
 * TIMOCOM i Trans.eu (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 18a:
 * „konto tradera imienne”). Pozycję dokłada `CustomUserMenu` za
 * przełącznikiem `exchangePersonalAccount` i uprawnieniem Trade.
 *
 * **Formularz tylko do zapisu.** Zapisanej wartości nie widać nigdy — pole
 * pokazuje maskę i datę zapisu; puste pole zostawia wartość bez zmian.
 * Wszystkie pola są typu hasło: login API i numer klienta też są danymi
 * dostępowymi, a okno bywa otwierane przy innych.
 */
export const MyExchangeAccountsMenuItem = () => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<MyExchangeAccount[]>([]);
  /** Wpisane wartości per giełda — nigdy nie przychodzą z serwera. */
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});

  const openDialog = () => {
    setOpen(true);
    setError(null);
    setDrafts({});
    setLoading(true);
    fetchMyExchangeAccounts()
      .then(setAccounts)
      .catch((e) => setError(toErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  const setValue = (exchange: string, field: string, value: string) =>
    setDrafts((d) => ({ ...d, [exchange]: { ...(d[exchange] ?? {}), [field]: value } }));

  const run = async (action: () => Promise<MyExchangeAccount[]>, success: string, exchange: string) => {
    setBusy(true);
    setError(null);
    try {
      setAccounts(await action());
      setDrafts((d) => ({ ...d, [exchange]: {} }));
      notify(success, { type: 'success' });
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <MenuItem onClick={openDialog}>
        <ListItemIcon>
          <KeyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Moje konta giełdowe</ListItemText>
      </MenuItem>
      <Dialog
        open={open}
        onClose={() => !busy && setOpen(false)}
        fullWidth
        maxWidth="sm"
        // Klawisze zostają w oknie — menu pod spodem przeskakiwało po literach.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Moje konta giełdowe</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Oferty, które wystawiasz, idą z Twojego konta na giełdzie. Zapisanych wartości nie da się tu
            odczytać — puste pole zostawia zapisaną wartość bez zmian.
          </DialogContentText>
          {loading && <Typography variant="body2">Wczytywanie…</Typography>}
          <Stack spacing={3}>
            {accounts.map((a) => {
              const draft = drafts[a.exchange] ?? {};
              const anyFilled = Object.values(draft).some((v) => v !== '');
              const anySaved = a.fields.some((f) => f.saved);
              return (
                <Box key={a.exchange} component="section" aria-label={a.label}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">{a.label}</Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={a.complete ? 'success' : 'warning'}
                      label={a.complete ? 'komplet' : 'niekompletne'}
                    />
                  </Stack>
                  <Stack spacing={1.5}>
                    {a.fields.map((f) => (
                      <TextField
                        key={f.field}
                        size="small"
                        type="password"
                        autoComplete="new-password"
                        label={f.label}
                        value={draft[f.field] ?? ''}
                        placeholder={f.saved ? SAVED_MASK : ''}
                        helperText={f.saved ? `zapisane${f.updatedAt ? ` ${shortDateTime(f.updatedAt)}` : ''}` : 'nie wpisano'}
                        disabled={busy}
                        onChange={(e) => setValue(a.exchange, f.field, e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    ))}
                  </Stack>
                  {a.companyNote && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {a.companyNote}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy || !anyFilled}
                      onClick={() =>
                        void run(() => saveMyExchangeAccount(a.exchange, draft), `Zapisano konto ${a.label}`, a.exchange)
                      }
                    >
                      Zapisz {a.label}
                    </Button>
                    {anySaved && (
                      <Button
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() =>
                          void run(() => removeMyExchangeAccount(a.exchange), `Usunięto konto ${a.label}`, a.exchange)
                        }
                      >
                        Usuń konto {a.label}
                      </Button>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={busy} onClick={() => setOpen(false)}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
